import { Grid } from '@vapor/material'
import { useEffect, useMemo, useState } from 'react'
import { CustomerDetailFragment, CustomerType, ReferenceCustomerFragment, State } from 'src/api/api.generated'
import ReferenceCustomerLookup from 'src/app/components/customer-lookup/reference-customer-lookup'
import CustomerPicker from 'src/app/components/customer-picker/customer-picker'
import * as R from 'ramda'
import useLocalizeString from 'src/app/hooks/current-locale/use-localized-string'
import { FieldError } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { DocumentCustomerFormSchema } from './document-customer-schema'
import ReadOnlyField from '../read-only-field/read-only-field'
import React from 'react'
import CustomerDetail from 'src/app/pages/common/customer/customer-detail'
import { FocusedField } from 'src/app/pages/common/customer/form/customer.form.types'

export type DocumentCustomerError = {
  id?: FieldError
  fiscalCode?: FieldError
  vatNumber?: FieldError
  countryCode?: FieldError
  municipalityId?: FieldError
  address?: FieldError
  city?: FieldError
}

export type DocumentCustomerInputProps = {
  referenceCustomers: ReferenceCustomerFragment[]
  value: DocumentCustomerFormSchema | null
  customerCurrentData: ReferenceCustomerFragment | null
  onChange: (value: DocumentCustomerFormSchema) => void
  error?: FieldError | DocumentCustomerError
  readonly?: boolean
  syncData: boolean
  dataSynched: () => void
  createReceiptZero?: boolean
}

export default function DocumentCustomerInput({ referenceCustomers, value, customerCurrentData, onChange, syncData, dataSynched, error, readonly, createReceiptZero }: DocumentCustomerInputProps) {
  const { t } = useTranslation()

  const customers = useMemo(() => {
    return R.uniqBy(
      (c) => c.id,
      referenceCustomers.filter((c) => c.auditable.state === State.ACTIVE),
    )
  }, [referenceCustomers])

  const [customer, setCustomer] = useState<(ReferenceCustomerFragment & { fromExternal: boolean }) | null>(null)
  const localizeString = useLocalizeString()

  const [localValue, setLocalValue] = useState<((ReferenceCustomerFragment | { id: string }) & { fromExternal: boolean }) | null>(null)

  useEffect(() => {
    let newLocalValue: ((ReferenceCustomerFragment | { id: string }) & { fromExternal: boolean }) | null = null
    if (!value) {
      newLocalValue = null
      setCustomer(null)
    } else {
      if (value.name) {
        const frag: ReferenceCustomerFragment = {
          id: value.id,
          name: value.name,
          fiscalCode: value.fiscalCode,
          vatNumber: value.vatNumber,
          type: referenceCustomers.find((c) => c.id === value.id)?.type ?? customer?.type ?? CustomerType.PERSON,
          mailContacts: [{ isDefault: true, value: value.email ?? '', order: 1, type: 'MAIN' }],
          auditable: { state: 'ACTIVE' },
          language: 'it',
          address: referenceCustomers.find((rc) => rc.id === value.id)?.address,
        }
        newLocalValue = { ...frag, fromExternal: false }
        if (customer?.id !== value.id || syncData) {
          dataSynched()
          setCustomer({ ...frag, fromExternal: false })
        }
      } else {
        newLocalValue = { id: value.id, fromExternal: false }
      }
    }
    setLocalValue(newLocalValue)
  }, [value?.id ? JSON.stringify(value) : null, syncData])

  const objectError = error && 'type' in error ? error : undefined
  const customerError = error && !('type' in error) ? error : undefined

  const [customerDialog, setCustomerDialog] = useState<{ customerId: string; focusedField: FocusedField } | null>(null)

  return (
    <>
      <Grid container spacing={1}>
        <Grid item xs={12} style={{ display: readonly ? 'none' : undefined }}>
          <CustomerPicker //
            customers={customers}
            pickedCustomerId={localValue?.id || null}
            onCustomerPicked={(customerId) => {
              const pickCustomer = referenceCustomers.find((c) => c.id === customerId) || null
              setCustomer(pickCustomer ? { ...pickCustomer, fromExternal: true } : null)
              onChange(customerFragmentToSchema(pickCustomer))
            }}
          />
        </Grid>
        <Grid item xs={12} style={{ display: readonly ? 'none' : undefined }}>
          <ReferenceCustomerLookup
            value={customer || localValue}
            onChange={(valueChanged) => {
              setCustomer(valueChanged ? { ...valueChanged, fromExternal: false } : null)
              onChange(customerFragmentToSchema(valueChanged))
            }}
            createReceiptZero={createReceiptZero}
          />
        </Grid>
        <Grid item xs={4}>
          <ReadOnlyField //
            label={t('pms.common.customer.name')}
            value={customer?.name}
            updatedValue={customerCurrentData?.name}
            error={objectError?.message || customerError?.id?.message}
          />
        </Grid>
        <Grid item xs={4}>
          <ReadOnlyField //
            label={t('pms.common.customer.fiscal_code')}
            value={customer?.fiscalCode}
            updatedValue={customerCurrentData?.fiscalCode}
            error={customerError?.fiscalCode?.message}
            clickable={customer?.id !== undefined && !readonly}
            onClick={() => {
              if (customer?.id) {
                setCustomerDialog({ customerId: customer.id, focusedField: 'FISCAL_CODE' })
              }
            }}
          />
        </Grid>
        <Grid item xs={4}>
          <ReadOnlyField //
            label={t('pms.common.customer.vat_number')}
            value={customer?.vatNumber}
            updatedValue={customerCurrentData?.vatNumber}
            error={customerError?.vatNumber?.message}
            clickable={customer?.id !== undefined && !readonly}
            onClick={() => {
              if (customer?.id) {
                setCustomerDialog({ customerId: customer.id, focusedField: 'VAT_NUMBER' })
              }
            }}
          />
        </Grid>
        <Grid item xs={4}>
          <ReadOnlyField //
            label={t('commons.address.country')}
            value={localizeString(customer?.address?.country?.name)}
            updatedValue={localizeString(customerCurrentData?.address?.country?.name)}
            error={customerError?.countryCode?.message}
            clickable={customer?.id !== undefined && !readonly}
            onClick={() => {
              if (customer?.id) {
                setCustomerDialog({ customerId: customer.id, focusedField: 'ADDRESS' })
              }
            }}
          />
        </Grid>{' '}
        <Grid item xs={4} sx={{ display: customer?.address?.country?.isoCode === 'IT' ? 'inherit' : 'none' }}>
          <ReadOnlyField //
            label={t('commons.address.municipality')}
            value={customer?.address?.municipality?.name}
            updatedValue={customerCurrentData?.address?.municipality?.name}
            error={customerError?.municipalityId?.message}
            clickable={customer?.id !== undefined && !readonly}
            onClick={() => {
              if (customer?.id) {
                setCustomerDialog({ customerId: customer.id, focusedField: 'ADDRESS' })
              }
            }}
          />
        </Grid>
        <Grid item xs={4} sx={{ display: customer?.address?.country?.isoCode === 'IT' ? 'none' : 'inherit' }}>
          <ReadOnlyField //
            label={t('commons.address.city')}
            value={customer?.address?.city}
            updatedValue={customerCurrentData?.address?.city?.name}
            error={customerError?.city?.message}
            clickable={customer?.id !== undefined && !readonly}
            onClick={() => {
              if (customer?.id) {
                setCustomerDialog({ customerId: customer.id, focusedField: 'ADDRESS' })
              }
            }}
          />
        </Grid>
        <Grid item xs={4}>
          <ReadOnlyField //
            label={t('commons.address.address')}
            value={customer?.address?.address}
            updatedValue={customerCurrentData?.address?.address}
            error={customerError?.address?.message}
            clickable={customer?.id !== undefined && !readonly}
            onClick={() => {
              if (customer?.id) {
                setCustomerDialog({ customerId: customer.id, focusedField: 'ADDRESS' })
              }
            }}
          />
        </Grid>
      </Grid>

      {customerDialog !== null && (
        <CustomerDetailMemo //
          customerId={customerDialog.customerId}
          focusedField={customerDialog.focusedField}
          open={customerDialog !== null}
          onClose={() => setCustomerDialog(null)}
          onUpserted={(customer) => {
            setCustomer(customer ? { ...customer, fromExternal: true } : null)
            onChange(customerFragmentToSchema(customer))
            setCustomerDialog(null)
          }}
        />
      )}
    </>
  )
}

export function customerFragmentToSchema(value: ReferenceCustomerFragment | null): DocumentCustomerFormSchema {
  const formValue: DocumentCustomerFormSchema = value
    ? {
        fiscalCode: value.fiscalCode || undefined,
        vatNumber: value.vatNumber || undefined,
        countryCode: value.address?.country?.isoCode,
        municipalityId: value.address?.municipality?.id,
        city: value.address?.city || '',
        postalCode: value.address?.postalCode || '',
        province: value.address?.province || '',
        id: value.id,
        address: value.address?.address || '',
        name: value.name || '',
        email: value.mailContacts?.find((x) => x.type === 'MAIN')?.value ?? value.mailContacts?.length ? value.mailContacts[0].value : undefined,
        splitPayment: value.company?.splitPayment ?? false,
        sdiCode: value.company?.sdiCode ?? '',
      }
    : {
        id: '',
        countryCode: '',
        address: '',
        splitPayment: false,
      }
  return formValue
}

type CustomerDetailMemoProps = {
  customerId: string
  open: boolean
  onClose: () => void
  onUpserted: (customerDetail: CustomerDetailFragment) => void
  focusedField: FocusedField
}

const CustomerDetailMemo = React.memo(
  (props: CustomerDetailMemoProps) => {
    const { open, onClose, onUpserted, ...otherProps } = props
    return (
      <CustomerDetail
        {...otherProps}
        appearance={{
          open: open,
          type: 'DIALOG',
          onClose: onClose,
          maxWidth: '800px',
        }}
        onCancel={onClose}
        onUpserted={onUpserted}
      />
    )
  },
  (prev, next) => {
    if (prev.open !== next.open) {
      return false
    }
    if (prev.onClose !== next.onClose) {
      return false
    }
    if (prev.onUpserted !== next.onUpserted) {
      return false
    }
    if ('customerId' in prev) {
      if ('customerId' in next) {
        return prev.customerId === next.customerId
      } else {
        return false
      }
    } else {
      return false
    }
  },
)
